/**
 * VCI-P1 Slice 2 — Internal Venue Partner Applications List
 * Route: /venue-commerce/partner-applications
 *
 * Internal UAT only. Lists all venue partner applications with lifecycle tracking.
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { hasCapability } from "@/services/api";
import axios from "axios";
import {
  FileText,
  Plus,
  Search,
  RefreshCw,
  Shield,
  CheckCircle,
  Clock,
  XCircle,
  Eye,
  AlertTriangle,
  SlidersHorizontal,
  Loader2,
  ChevronRight,
  MapPin,
  Hotel,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";

const API = process.env.REACT_APP_BACKEND_URL;

const STAGE_CONFIG = {
  APPLIED:       { label: "Applied",        icon: Clock,        cls: "bg-blue-50 text-blue-700 border-blue-200" },
  UNDER_REVIEW:  { label: "Under Review",   icon: Eye,          cls: "bg-amber-50 text-amber-700 border-amber-200" },
  INFO_REQUESTED:{ label: "Info Requested", icon: AlertTriangle, cls: "bg-orange-50 text-orange-700 border-orange-200" },
  APPROVED:      { label: "Approved",       icon: CheckCircle,  cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  REJECTED:      { label: "Rejected",       icon: XCircle,      cls: "bg-red-50 text-red-700 border-red-200" },
};

function StageBadge({ stage }) {
  const cfg = STAGE_CONFIG[stage] || { label: stage, icon: Clock, cls: "bg-gray-50 text-gray-600 border-gray-200" };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border font-medium ${cfg.cls}`}>
      <Icon size={11} /> {cfg.label}
    </span>
  );
}

export default function VenuePartnerApplications() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = hasCapability(user, "supplier.manage");
  const [apps, setApps] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [stage, setStage] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { limit: 100 };
      if (search.trim()) params.search = search.trim();
      if (stage) params.stage = stage;
      const res = await axios.get(`${API}/api/venue-commerce/partner-applications`, { params, withCredentials: true });
      setApps(res.data.results || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load applications");
    } finally {
      setLoading(false);
    }
  }, [search, stage]);

  useEffect(() => {
    const t = setTimeout(fetch, 300);
    return () => clearTimeout(t);
  }, [fetch]);

  // Stage counts
  const counts = apps.reduce((acc, a) => { acc[a.stage] = (acc[a.stage] || 0) + 1; return acc; }, {});

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center shadow-sm">
            <FileText size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900" data-testid="vci-partner-apps-title">
              Partner Applications
            </h1>
            <p className="text-sm text-gray-500 flex items-center gap-1 mt-0.5">
              <Shield size={11} className="text-amber-500" />
              Internal UAT — external applications disabled
            </p>
          </div>
        </div>
        {canManage && (
          <Button
            size="sm"
            onClick={() => navigate("/venue-commerce/partner-applications/new")}
            className="gap-2"
            data-testid="vci-new-application-btn"
          >
            <Plus size={14} /> New Application
          </Button>
        )}
      </div>

      {/* Stage summary pills */}
      <div className="flex flex-wrap gap-2">
        {Object.entries(STAGE_CONFIG).map(([key, cfg]) => {
          const count = counts[key] || 0;
          const Icon = cfg.icon;
          return (
            <button
              key={key}
              onClick={() => setStage(stage === key ? "" : key)}
              className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border font-medium transition-all ${
                stage === key ? cfg.cls + " ring-2 ring-offset-1 ring-current" : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
              }`}
              data-testid={`vci-stage-filter-${key}`}
            >
              <Icon size={11} /> {cfg.label}
              {count > 0 && (
                <span className={`ml-0.5 w-4 h-4 rounded-full text-white flex items-center justify-center text-xs
                  ${key === "APPLIED" ? "bg-blue-600" : key === "UNDER_REVIEW" ? "bg-amber-600" :
                    key === "INFO_REQUESTED" ? "bg-orange-600" : key === "APPROVED" ? "bg-emerald-600" : "bg-red-600"}`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm flex items-center gap-3">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search by applicant, venue, email or application number..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 h-9 text-sm"
            data-testid="vci-apps-search"
          />
        </div>
        <Button variant="ghost" size="sm" className="h-9 px-2" onClick={fetch} data-testid="vci-apps-refresh">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </Button>
      </div>

      {/* Count */}
      <p className="text-sm text-gray-500" data-testid="vci-apps-count">
        {loading ? "Loading…" : `${total} application${total !== 1 ? "s" : ""}${stage ? ` — ${STAGE_CONFIG[stage]?.label}` : ""}`}
      </p>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm" data-testid="vci-apps-error">
          {error}
        </div>
      )}

      {loading && apps.length === 0 ? (
        <div className="flex items-center justify-center h-40 text-gray-400 gap-2">
          <Loader2 size={18} className="animate-spin" />
          <span className="text-sm">Loading…</span>
        </div>
      ) : apps.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-48 text-gray-400 gap-3">
          <FileText size={36} className="opacity-30" />
          <p className="text-sm font-medium">No applications</p>
          <p className="text-xs text-gray-400">Create a new internal application to get started</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
          <table className="w-full text-sm" data-testid="vci-apps-table">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Application</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Venue</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Applicant</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Stage</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Applied</th>
                <th className="px-4 py-3 w-8"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {apps.map(app => (
                <ApplicationRow
                  key={app.id}
                  app={app}
                  onClick={() => navigate(`/venue-commerce/partner-applications/${app.id}`)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ApplicationRow({ app, onClick }) {
  const dup = app.duplicate_check || {};
  const highDup = (dup.matches || []).some(m => m.confidence === "HIGH");

  return (
    <tr
      className="hover:bg-indigo-50/30 cursor-pointer transition-colors"
      onClick={onClick}
      data-testid={`vci-app-row-${app.id}`}
    >
      <td className="px-4 py-3">
        <p className="font-mono text-xs font-semibold text-indigo-700">{app.application_number}</p>
        {highDup && (
          <span className="inline-flex items-center gap-1 text-xs text-orange-600 font-medium mt-0.5">
            <AlertTriangle size={10} /> Duplicate match
          </span>
        )}
      </td>
      <td className="px-4 py-3">
        <p className="font-medium text-gray-900 truncate max-w-xs">{app.venue_name}</p>
        <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
          <span className="flex items-center gap-1"><MapPin size={10} />{app.venue_city}</span>
          {app.venue_type && <span className="flex items-center gap-1"><Hotel size={10} />{app.venue_type.replace(/_/g, " ")}</span>}
        </div>
      </td>
      <td className="px-4 py-3">
        <p className="text-gray-700">{app.applicant_name}</p>
        <p className="text-xs text-gray-400">{app.applicant_email}</p>
      </td>
      <td className="px-4 py-3">
        <StageBadge stage={app.stage} />
      </td>
      <td className="px-4 py-3 text-xs text-gray-400">
        {new Date(app.applied_at).toLocaleDateString()}
      </td>
      <td className="px-4 py-3">
        <ChevronRight size={14} className="text-gray-300" />
      </td>
    </tr>
  );
}
